import { authFetch } from '../../auth/authFetch';
import { getApiBaseUrl } from '../../auth/api';
import type {
  ApiResponse,
  CreatePayGroupDto,
  CreatePayPeriodDto,
  PayGroupDto,
  PayGroupStatus,
  PayPeriodDto,
  UpdatePayGroupDto,
  UpdatePayPeriodDto,
} from './organisationApiTypes';

const base = () => `${getApiBaseUrl()}/api`;
const pageSize = 100;

const parse = async <T>(response: Response): Promise<T> => {
  const body = await response.json() as ApiResponse<T>;
  if (!response.ok || !body.success || body.result == null)
    throw new Error(body.message || `Request failed: ${response.status}`);
  return body.result;
};

const json = (method: 'POST' | 'PATCH', body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

const listAll = async <T>(urlForPage: (take: number, skip: number) => string): Promise<T[]> => {
  const result: T[] = [];
  let skip = 0;

  while (true) {
    const page = await parse<T[]>(await authFetch(urlForPage(pageSize, skip)));
    result.push(...page);
    if (page.length < pageSize)
      return result;
    skip += page.length;
  }
};

export const listPayGroups = async (
  organisationId: string,
  status: PayGroupStatus = 'active',
): Promise<PayGroupDto[]> => {
  return listAll<PayGroupDto>((take, skip) => {
    const query = new URLSearchParams({
      organisationId,
      status,
      take: String(take),
      skip: String(skip),
    });
    return `${base()}/paygroup?${query}`;
  });
};

export const getPayGroup = async (payGroupId: string): Promise<PayGroupDto> =>
  parse<PayGroupDto>(await authFetch(`${base()}/paygroup/${encodeURIComponent(payGroupId)}`));

export const createPayGroup = async (request: CreatePayGroupDto): Promise<PayGroupDto> =>
  parse<PayGroupDto>(await authFetch(`${base()}/paygroup`, json('POST', request)));

export const updatePayGroup = async (payGroupId: string, request: UpdatePayGroupDto): Promise<PayGroupDto> =>
  parse<PayGroupDto>(await authFetch(`${base()}/paygroup/${encodeURIComponent(payGroupId)}`, json('PATCH', request)));

export const listPayPeriods = async (payGroupId: string): Promise<PayPeriodDto[]> =>
  listAll<PayPeriodDto>(
    (take, skip) => `${base()}/paygroup/${encodeURIComponent(payGroupId)}/payperiod?take=${take}&skip=${skip}`,
  );

export const createPayPeriod = async (payGroupId: string, request: CreatePayPeriodDto): Promise<PayPeriodDto> =>
  parse<PayPeriodDto>(await authFetch(`${base()}/paygroup/${encodeURIComponent(payGroupId)}/payperiod`, json('POST', request)));

export const getPayPeriod = async (payPeriodId: string): Promise<PayPeriodDto> =>
  parse<PayPeriodDto>(await authFetch(`${base()}/payperiod/${encodeURIComponent(payPeriodId)}`));

export const updatePayPeriod = async (payPeriodId: string, request: UpdatePayPeriodDto): Promise<PayPeriodDto> =>
  parse<PayPeriodDto>(await authFetch(`${base()}/payperiod/${encodeURIComponent(payPeriodId)}`, json('PATCH', request)));
